import { AuthContextType } from '../contexts/AuthContext';
import React from 'react';

/**
 * Azure API Client with automatic Azure token injection
 * Uses azureAccessToken from AuthContext for Azure service calls
 * Handles token expiration and automatic refresh
 * Only works with exchange-token flow (requires azureAccessToken)
 */
class AzureApiClient {
  private getTokenFunction: (() => string | null) | null = null;
  private checkTokenExpiryFunction: (() => boolean) | null = null;
  private refreshTokensFunction: (() => Promise<boolean>) | null = null;
  private getUserInfoFunction: (() => { email: string; name: string } | null) | null = null;
  private isInitialized = false;
  private initializationResolvers: Array<() => void> = [];

  /**
   * Set the token management functions
   * These functions will be called by the API client when needed
   *
   * @param getToken - Function to retrieve the current Azure token from memory
   * @param checkExpiry - Function to check if current token is expired
   * @param refreshTokens - Function to refresh expired tokens (only for exchange-token flow)
   * @param getUserInfo - Function to get user information (email and name)
   */
  setTokenProvider(
    getToken: () => string | null,
    checkExpiry: () => boolean,
    refreshTokens: () => Promise<boolean>,
    getUserInfo: () => { email: string; name: string } | null,
  ) {
    this.getTokenFunction = getToken;
    this.checkTokenExpiryFunction = checkExpiry;
    this.refreshTokensFunction = refreshTokens;
    this.getUserInfoFunction = getUserInfo;
    this.isInitialized = true;
    
    // Resolve all waiting promises
    this.initializationResolvers.forEach((resolve) => resolve());
    this.initializationResolvers = [];
  }

  /**
   * Wait for API client initialization
   * Returns immediately if already initialized
   * Otherwise, returns a promise that resolves when setTokenProvider is called
   */
  private async waitForInitialization(): Promise<void> {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      this.initializationResolvers.push(resolve);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        reject(new Error('Azure API client initialization timeout. useInitializeAzureApiClient() was not called.'));
      }, 5000);
    });
  }

  /**
   * Check if refresh capability is available
   */
  hasRefreshCapability(): boolean {
    return this.refreshTokensFunction !== null;
  }

  /**
   * Fetch wrapper that automatically injects Azure authorization token
   * Handles token expiration and automatic refresh
   * Only works with exchange-token flow (requires azureAccessToken)
   */
  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Wait for initialization to complete
    await this.waitForInitialization();

    // Check if token is expired
    if (this.checkTokenExpiryFunction?.()) {
      // Try to refresh only if refresh function is available (exchange-token flow)
      if (this.refreshTokensFunction) {
        console.log('Azure token expired, attempting refresh...');
        const refreshed = await this.refreshTokensFunction();
        if (!refreshed) {
          throw new Error('Azure session expired. Please log in again.');
        }
      } else {
        // check-token flow: no Azure token available
        throw new Error(
          'Azure token not available. Please use refresh_token flow.',
        );
      }
    }

    const token = this.getTokenFunction?.();

    if (!token) {
      throw new Error(
        'No Azure authentication token available. Azure tokens are only provided in exchange-token flow.',
      );
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 - token might be invalid
    if (response.status === 401) {
      console.log('Received 401 Unauthorized from Azure service');
      
      // Try to refresh token only if refresh function exists (exchange-token flow)
      if (this.refreshTokensFunction) {
        console.log('Attempting Azure token refresh...');
        const refreshed = await this.refreshTokensFunction();
        if (refreshed) {
          // Retry the request with new token
          const newToken = this.getTokenFunction?.();
          if (newToken) {
            const retryHeaders = {
              ...options.headers,
              Authorization: `Bearer ${newToken}`,
            };

            return fetch(url, {
              ...options,
              headers: retryHeaders,
            });
          }
        }
      }

      // For check-token flow or failed refresh
      throw new Error(
        'Azure authentication failed. Session expired, please log in again.',
      );
    }

    return response;
  }

  /**
   * Get the current Azure token (useful for non-fetch calls)
   */
  getAuthToken(): string | null {
    return this.getTokenFunction?.() || null;
  }

  /**
   * Get Azure authorization header object
   */
  getAuthHeader(): { Authorization: string } | null {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : null;
  }

  /**
   * Get user information (email and name)
   * @returns User info object or null if not available
   */
  getUserInfo(): { email: string; name: string } | null {
    return this.getUserInfoFunction?.() || null;
  }
}

export const azureApiClient = new AzureApiClient();

/**
 * Hook to initialize Azure API client with token management functions
 * Call this in your root App component or AtlasmapProvider
 */
export function useInitializeAzureApiClient(authContext: AuthContextType) {
  React.useMemo(() => {
    azureApiClient.setTokenProvider(
      authContext.getAzureAccessToken,
      authContext.isAzureTokenExpired,
      authContext.refreshTokens,
      authContext.getUserInfo,
    );
  }, [
    authContext.getAzureAccessToken,
    authContext.isAzureTokenExpired,
    authContext.refreshTokens,
    authContext.getUserInfo,
  ]);
}
